import React, {useEffect, useState} from "react";
import _ from "lodash";
import {toast} from "react-toastify";
import {makeApiCall, makeApiCallPost} from "../../../Api/makeApiCall";
import ReactTable from "../../Components/ReactTable";
import {SELECT_COLUMN} from "../../Components/ReactTable/CheckboxColumnComponent";
import RetailerIdAndName from "../../Components/RetailerIdAndName";
import {getRetailerName} from "../../../utils/localCache/retailerMapCache";
import {sortAscendingWithNullsLast} from "../../../utils/sorting";
import {getNicelyFormattedNumber} from "../../../utils/formatUtil";
import {setValuesToNullIfEmptyOrUndefined} from "../../Components/ReactTable/helpers";
import {toastError, toastSuccess} from "../../../utils/toast";
import ReactTableFilterDropdown from "../../ReactTable/ReactTableFilterDropdown";
import checkCircle from "../../../assets/check-circle.svg";
import {LoadingWrapper} from "../../Components/LoadingWrapper";


const columns = [
    SELECT_COLUMN,
    {
        header: "Store",
        id: 'store',
        accessorKey: 'store',
        minSize: 300,
        filterFn: (row, columnId, filterValue) => {
            return !filterValue || Boolean(`${row.original.store.toLowerCase()}`.includes(filterValue.toLowerCase()));
        }
    },
    {
        header: "Is Closed?",
        id: "closed",
        accessorKey: 'closed',
        size: 64,
        filter: (theseProps) => {
            return <ReactTableFilterDropdown
                id={"closed"}
                options={[{value: "1", label: "1"}, {value: "0", label: "0"}]}
                onChange={(selected) => theseProps.column.setFilterValue(selected)}
                selectedValue={theseProps.column.getFilterValue()}
                isClearable={true}
            />
        }
    },
    {
        header: "Retailer",
        id: "retailer",
        accessorKey: 'retailer',
        minSize: 100,
        cell: ({row}) => <RetailerIdAndName retailerId={row.original.retailerId}/>,
        filterFn: (row, columnId, filterValue) => {
            return !filterValue || Boolean(`${row.original.retailerId} - ${row.original.retailerName.toLowerCase()}`.includes(filterValue.toLowerCase()));
        },
        sortDescFirst: false,
        sortingFn: (rowA, rowB) => {
            const rowBValue = rowB.original.retailerId;
            const rowAValue = rowA.original.retailerId;
            return sortAscendingWithNullsLast(rowAValue, rowBValue);
        }
    },
    {
        header: "City",
        id: "city",
        accessorKey: 'city'
    },
    {
        header: "State",
        id: "state",
        accessorKey: 'state',
        size: 164,
        filterFn: (row, columnId, filterValue) => {
            return _.isEmpty(filterValue) || _.includes(filterValue, row.original.state)
        },
        filter: (theseProps) => {
            const uniqueStates = _.uniqBy(theseProps.table.getCoreRowModel().rows
                .map(row => row.original.state)
                .filter(state => state !== '')
                .sort())
            const statesOptionsSorted = uniqueStates.map(state => {
                return {label: state, value: state}
            })
            return <ReactTableFilterDropdown
                id={theseProps.column.id}
                options={statesOptionsSorted}
                onChange={(selected) => theseProps.column.setFilterValue(selected)}
                selectedValues={theseProps.column.getFilterValue()}
                isMulti
                isClearable={true}
                placeholder={''}
            />
        }
    },
    {
        header: "Zip Code",
        id: "zip1",
        accessorKey: 'zip1'
    }
];

function StoreParticipationBulkAdd({onCancel, onSubmitted}) {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [stopDate, setStopDate] = useState('');
    const [startDate, setStartDate] = useState('');

    useEffect(() => {
        setLoading(true);
        makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/stores/`).then((storesResult) => {
            const mappedStoreData = storesResult.data.map((result) => {
                return {
                    retailerId: result.retailerId,
                    retailerName: getRetailerName(result.retailerId.toString()),
                    store: `${result.storeId} - ${result.storeName}`,
                    closed: result.closed,
                    city: result.city,
                    state: result.state,
                    zip1: result.zip1,
                    storeId: result.storeId
                }
            });
            setTableData(mappedStoreData);
            setLoading(false);
        });
    }, []);

    async function submitSelectedStores(selectedRows) {
        setSubmitting(true);
        const responses = [];
        // Submitted one row at a time (not multiple_submits) so each request re-reads the file after the
        // previous row saved -- this is the same request shape every single-row "Add" already uses, and
        // it's what lets the server correctly auto-increment each new row's index.
        for (const row of selectedRows) {
            const rowToPost = setValuesToNullIfEmptyOrUndefined({
                storeId: row.original.storeId,
                stopDate,
                startDate
            });
            responses.push(await makeApiCallPost(
                `${process.env.REACT_APP_EDP_API_BASE_URL}/storeParticipation/?single_row_action=update`,
                rowToPost
            ));
        }
        setSubmitting(false);
        if (responses.some(response => response?.apiError)) {
            toastError(toast, `Operation failed with api error "${JSON.stringify(responses.find(response => response?.apiError)?.apiError.message)}"`);
            return;
        }
        toastSuccess(toast, `Successfully added a participation window for ${selectedRows.length} store/s.`);
        onSubmitted();
    }

    return <LoadingWrapper loading={loading}>
        <div className={'store-participation-bulk-add-date-fields'}>
            <label htmlFor={'store-participation-bulk-stop-date'}>Stop Date</label>
            <input
                id={'store-participation-bulk-stop-date'}
                data-testid={'store-participation-bulk-stop-date'}
                type={'date'}
                className={'edit-form-date-input-field'}
                value={stopDate}
                onChange={event => setStopDate(event.target.value)}
            />
            <label htmlFor={'store-participation-bulk-start-date'}>Start Date</label>
            <input
                id={'store-participation-bulk-start-date'}
                data-testid={'store-participation-bulk-start-date'}
                type={'date'}
                className={'edit-form-date-input-field'}
                value={startDate}
                onChange={event => setStartDate(event.target.value)}
            />
            <button type={'button'} className={'button tertiary'} onClick={onCancel}>Cancel</button>
        </div>
        <ReactTable
            filterable={true}
            data={tableData}
            columns={columns}
            initialState={{pagination: {pageSize: 50}}}
        >
            {
                ({table}) => {
                    const totalRows = getNicelyFormattedNumber(table.getFilteredRowModel().rows.length);
                    const selectedRows = table.getSelectedRowModel().rows;
                    const isAllRowsSelected = table.getIsAllRowsSelected();
                    const selectAllButtonText = `Select all ${totalRows} stores`;
                    const allRowsSelectedText = `${totalRows} stores selected`;
                    const canSubmit = selectedRows.length > 0 && Boolean(stopDate) && !submitting;
                    return (
                        <div className={'designation-action-buttons-container'}
                             data-testid={"action-button-container"}>
                            {isAllRowsSelected ?
                                <>
                                    <img alt={'status'} className={'success-image'} src={checkCircle}/>
                                    <div data-testid={'all-stores-selected'}>{allRowsSelectedText}</div>
                                </>
                                :
                                <button data-testid={'select-all-stores'} type={"button"} className={"button tertiary"}
                                        onClick={() => table.toggleAllRowsSelected()}
                                >{selectAllButtonText}</button>}
                            <button disabled={!canSubmit} type={"button"} className={"button primary"}
                                    data-testid={'submit-selected-stores'}
                                    onClick={() => submitSelectedStores(selectedRows)}>
                                Add window to selected stores
                            </button>
                        </div>
                    );
                }
            }
        </ReactTable>
    </LoadingWrapper>;
}

export default StoreParticipationBulkAdd;
